import { useState } from 'react'
import { Alert, Group, Stack } from '@mantine/core'
import { AppButton, AppModal, AppPasswordInput, AppText } from '../../app/common/ui'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'

interface DeleteAccountModalProps {
  opened: boolean
  onClose: () => void
}

export default function DeleteAccountModal ({ opened, onClose }: DeleteAccountModalProps) {
  const { userStore } = useStore()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const handleClose = () => {
    setPassword('')
    setError(null)
    setSent(false)
    onClose()
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    try {
      await agent.Account.requestDeletion(password.trim() || undefined)
      setSent(true)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail ?? 'Could not start account deletion. Try again or email privacy@gunslop.com.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AppModal opened={opened} onClose={handleClose} title="Delete account" centered>
      <Stack spacing="md">
        {sent
          ? (
            <Alert color="green" title="Check your email">
              We sent a confirmation link to your registered email address. Your account stays active until you confirm.
              The link expires in 30 days.
            </Alert>
            )
          : (
            <>
              <AppText size="sm" color="dimmed">
                This starts a permanent deletion process. After you confirm by email, your profile is deactivated,
                personal data is removed, and remaining content may show as from &quot;Deleted User.&quot;
                Some ledger records may be retained in anonymized form where required by law.
              </AppText>
              {error && <Alert color="red">{error}</Alert>}
              <PasswordInput
                label="Password (recommended)"
                description="Confirm it is you. Leave blank only if you cannot use password verification."
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="current-password"
              />
              <Group position="right">
                <AppButton variant="default" onClick={handleClose}>Cancel</AppButton>
                <AppButton color="red" loading={loading} onClick={() => void handleSubmit()}>
                  Email me a confirmation link
                </AppButton>
              </Group>
            </>
            )}
        {sent && (
          <AppButton onClick={handleClose}>Close</AppButton>
        )}
      </Stack>
    </AppModal>
  )
}
